import { useMemo } from "react"
import type { TemplateSection } from "shared/api/templateService"
import { ALWAYS_EDITABLE_SECTION_ROLE_CODES, ALWAYS_EDITABLE_STATUS_CODES } from "../utils/pageHelpers"

/**
 * Encapsulates the visibility rules for workflow sections.
 * See docs/permit-request-refactor-plan.md for the full decision matrix.
 */
interface UseWorkflowSectionsParams {
  workflowSections: TemplateSection[]
  stageSequenceMap: Record<string, number> | null
  templateStageOrderMap: Record<string, number>
  activeStageSequence: number | null
  activeStageTemplateIndex: number
  normalizedActiveStageId: string | null
  normalizedActiveStageCode: string | null
  isReadOnly: boolean
  canApproveCurrentStage: boolean
  currentUserRoleCodeCandidates?: string[]
  permitStatus?: string
}

interface UseWorkflowSectionsResult {
  visibleWorkflowSections: TemplateSection[]
  shouldHideWorkflowSections: boolean
  isStageEditor: boolean
  isReadOnlyViewer: boolean
  isRequesterView: boolean
}

export const useWorkflowSections = ({
  workflowSections,
  stageSequenceMap,
  templateStageOrderMap,
  activeStageSequence,
  activeStageTemplateIndex,
  normalizedActiveStageId,
  normalizedActiveStageCode,
  isReadOnly,
  canApproveCurrentStage,
  currentUserRoleCodeCandidates = [],
  permitStatus,
}: UseWorkflowSectionsParams): UseWorkflowSectionsResult => {
  const isStageEditor = Boolean(canApproveCurrentStage)
  const isReadOnlyViewer = isReadOnly && !isStageEditor
  const isRequesterView = !isReadOnly && !isStageEditor

  const shouldHideWorkflowSections = useMemo(() => {
    if (isRequesterView) {
      return !normalizedActiveStageId && !normalizedActiveStageCode
    }
    return false
  }, [isRequesterView, normalizedActiveStageId, normalizedActiveStageCode])

  const visibleWorkflowSections = useMemo(() => {
    if (workflowSections.length === 0) {
      return []
    }

    const getSectionSequence = (section: TemplateSection): number | null => {
      const stageIdKey = section.approvalStageId?.trim().toLowerCase()
      const stageCodeKey = section.code?.trim().toUpperCase()

      const tryLookup = (key: string | null | undefined): number | undefined => {
        if (!key) {
          return undefined
        }
        if (stageSequenceMap && typeof stageSequenceMap[key] === "number") {
          return stageSequenceMap[key]
        }
        if (typeof templateStageOrderMap[key] === "number") {
          return templateStageOrderMap[key]
        }
        return undefined
      }

      const sequenceById = tryLookup(stageIdKey)
      if (typeof sequenceById === "number") {
        return sequenceById
      }

      const sequenceByCode = tryLookup(stageCodeKey)
      if (typeof sequenceByCode === "number") {
        return sequenceByCode
      }

      return null
    }

    const getSectionTemplateOrder = (section: TemplateSection): number | null => {
      const stageIdKey = section.approvalStageId?.trim().toLowerCase()
      const stageCodeKey = section.code?.trim().toUpperCase()
      if (stageIdKey && typeof templateStageOrderMap[stageIdKey] === "number") {
        return templateStageOrderMap[stageIdKey]
      }
      if (stageCodeKey && typeof templateStageOrderMap[stageCodeKey] === "number") {
        return templateStageOrderMap[stageCodeKey]
      }
      return null
    }

    const includeSectionUntilStage = (section: TemplateSection, includeCurrent: boolean): boolean => {
      if (!section.approvalStageId) {
        return true
      }

      const sectionSequence = getSectionSequence(section)
      if (activeStageSequence !== null && typeof sectionSequence === "number") {
        return includeCurrent ? sectionSequence <= activeStageSequence : sectionSequence < activeStageSequence
      }

      if (activeStageTemplateIndex !== -1) {
        const templateIndex = getSectionTemplateOrder(section)
        if (typeof templateIndex === "number") {
          return includeCurrent
            ? templateIndex <= activeStageTemplateIndex
            : templateIndex < activeStageTemplateIndex
        }
      }

      return includeCurrent ? activeStageSequence === null : false
    }

    let baseSections: TemplateSection[]
    if (isRequesterView) {
      baseSections = workflowSections.filter((section) => !section.approvalStageId)
    } else if (shouldHideWorkflowSections) {
      baseSections = []
    } else if (isReadOnlyViewer) {
      baseSections = workflowSections.filter((section) => includeSectionUntilStage(section, true))
    } else if (activeStageSequence === null && activeStageTemplateIndex === -1) {
      baseSections = workflowSections
    } else {
      baseSections = workflowSections.filter((section) => includeSectionUntilStage(section, true))
    }

    // IRPC-specific: ENQA (and any other role in ALWAYS_EDITABLE_SECTION_ROLE_CODES) can see/update
    // their monitoring section before the workflow reaches their own stage, scoped to
    // ALWAYS_EDITABLE_STATUS_CODES so it doesn't surface at every stage/status.
    const alwaysVisibleSections = permitStatus && ALWAYS_EDITABLE_STATUS_CODES.includes(permitStatus)
      ? workflowSections.filter((section) => {
          const sectionCode = section.code?.trim().toUpperCase() ?? ''
          return (
            ALWAYS_EDITABLE_SECTION_ROLE_CODES.includes(sectionCode) &&
            currentUserRoleCodeCandidates.includes(sectionCode)
          )
        })
      : []
    if (alwaysVisibleSections.length === 0) {
      return baseSections
    }
    const includedIds = new Set(baseSections.map((section) => section.templateSectionId))
    return [
      ...baseSections,
      ...alwaysVisibleSections.filter((section) => !includedIds.has(section.templateSectionId)),
    ]
  }, [
    workflowSections,
    stageSequenceMap,
    templateStageOrderMap,
    activeStageSequence,
    activeStageTemplateIndex,
    isRequesterView,
    isReadOnlyViewer,
    shouldHideWorkflowSections,
    currentUserRoleCodeCandidates,
    permitStatus,
  ])

  return {
    visibleWorkflowSections,
    shouldHideWorkflowSections,
    isStageEditor,
    isReadOnlyViewer,
    isRequesterView,
  }
}
